import React, { useEffect, useState } from "react";
import { Clock, Play, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtEUR, formatApiError } from "../../lib/api";
import { fmtTime, fmtDur, fmtClock } from "./dispoUtils";

const STARTABLE = ["Confermata", "Assegnata"];

function Cell({ label, value, tone = "text-slate-100", testid }) {
  return (
    <div className="rounded-lg bg-slate-950/70 border border-slate-800 p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">{label}</div>
      <div className={`font-mono-num font-extrabold text-lg mt-1 ${tone}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function useElapsed(startIso, active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) { return undefined; }
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return startIso ? (now - new Date(startIso).getTime()) / 1000 : 0;
}

export function DispoDriverPanel({ booking, shareRate, onChanged }) {
  const [b, setB] = useState(booking);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState(null);
  const running = !!b.dispo_started_at && !b.dispo_ended_at;
  const elapsed = useElapsed(b.dispo_started_at, running);
  useEffect(() => { setB(booking); }, [booking]);
  useEffect(() => {
    api.get(`/dispo/${booking.id}`).then((r) => { setB(r.data.booking); onChanged?.(r.data.booking); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  const act = async (kind) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post(`/dispo/${b.id}/${kind}`, kind === "end" ? { confirm: true } : {});
      setB(r.data.booking);
      onChanged?.(r.data.booking);
      if (r.data.already) toast.info(kind === "start" ? "Disposizione già iniziata" : "Disposizione già terminata");
      else toast.success(kind === "start" ? "Disposizione iniziata" : "Disposizione terminata e salvata");
      setConfirm(false);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
      api.get(`/dispo/${b.id}`).then((x) => setB(x.data.booking)).catch(() => {});
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent p-4 space-y-3" data-testid="dispo-driver-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-mono-num font-bold inline-flex items-center gap-1.5"><Clock size={12} /> DISPO · a durata non predeterminata</div>
        <span className="text-[10px] uppercase tracking-widest text-slate-300 whitespace-nowrap shrink-0" data-testid="dispo-status">{b.dispo_ended_at ? "Completata" : running ? "In corso" : b.status}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Cell label="La tua tariffa" value={b.dispo_driver_rate != null ? `${fmtEUR(b.dispo_driver_rate)} / ora` : "—"} tone="text-amber-300" testid="dispo-driver-rate" />
        <Cell label="Inizio previsto" value={b.time || "—"} />
        {b.dispo_started_at && <Cell label="Inizio effettivo" value={fmtTime(b.dispo_started_at)} tone="text-emerald-300" testid="dispo-actual-start" />}
        {running && <Cell label="Durata in corso" value={fmtClock(elapsed)} testid="dispo-elapsed" />}
        {b.dispo_ended_at && <Cell label="Fine effettiva" value={fmtTime(b.dispo_ended_at)} testid="dispo-actual-end" />}
        {b.dispo_ended_at && <Cell label="Durata effettiva" value={fmtDur(b.dispo_duration_min)} testid="dispo-duration" />}
        {b.dispo_ended_at && <Cell label="Ore fatturabili" value={b.dispo_billable_hours} testid="dispo-billable" />}
      </div>
      {b.dispo_ended_at && (
        <div className="grid grid-cols-2 gap-3" data-testid="dispo-driver-summary">
          <Cell label={b.dispo_driver_rate != null ? `Prezzo driver (${b.dispo_billable_hours} × ${fmtEUR(b.dispo_driver_rate)})` : "Prezzo driver (importo fisso)"} value={fmtEUR(b.driver_price || 0)} testid="dispo-driver-total" />
          <Cell label={`Il tuo compenso ${shareRate}%`} value={fmtEUR(b.driver_cost || 0)} tone="text-emerald-300" testid="dispo-driver-cost" />
        </div>
      )}
      {!b.dispo_started_at && !STARTABLE.includes(b.status) && (
        <div className="rounded-xl border border-slate-700 p-3 text-xs text-slate-400 text-center" data-testid="dispo-not-startable">
          Avvio disponibile quando la DISPO è confermata o assegnata (stato attuale: {b.status || "—"})
        </div>
      )}
      {!b.dispo_started_at && STARTABLE.includes(b.status) && (
        <button type="button" disabled={busy} onClick={() => act("start")} data-testid="dispo-start-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 font-bold tracking-wider bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Play size={18} />} INIZIA DISPOSIZIONE
        </button>
      )}
      {running && !confirm && (
        <button type="button" disabled={busy} onClick={() => { setErr(null); setConfirm(true); }} data-testid="dispo-end-btn"
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 font-bold tracking-wider bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-60">
          <Square size={16} /> TERMINA DISPOSIZIONE
        </button>
      )}
      {/* Conferma inline: un dialog in portal finiva sotto la scheda servizio (z-index) e il tocco non aveva effetto */}
      {running && confirm && (
        <div className="rounded-xl border border-rose-500/60 bg-rose-500/10 p-3 space-y-3" data-testid="dispo-end-confirm">
          <div className="text-sm text-white font-semibold">Terminare la disposizione?</div>
          <div className="text-xs text-slate-300">Verrà registrata l'ora di fine ({fmtTime(new Date().toISOString())}) e calcolato automaticamente il compenso.</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={busy} onClick={() => setConfirm(false)} data-testid="dispo-end-cancel"
              className="rounded-lg py-3 text-sm font-semibold border border-slate-600 text-slate-200 disabled:opacity-50">Annulla</button>
            <button type="button" disabled={busy} onClick={() => act("end")} data-testid="dispo-end-confirm-btn"
              className="rounded-lg py-3 text-sm font-bold bg-rose-600 hover:bg-rose-500 text-white inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={15} className="animate-spin" />}Sì, termina
            </button>
          </div>
        </div>
      )}
      {err && <div className="text-xs text-rose-300" data-testid="dispo-error">{err}</div>}
    </div>
  );
}
